import { callRpc } from "@/lib/api/rpc";

export interface RateQuote {
  carrier_service_id: string;
  carrier_code: string;
  service_name: string;
  zone: string;
  currency: string;
  cost: number;
  chargeable_weight_kg: number;
  eta_min_days: number | null;
  eta_max_days: number | null;
  rate_source?: string;
  carrier_account_id?: string | null;
  carrier_account_name?: string | null;
}

export interface QuoteRequestResult {
  ok: boolean;
  error?: string;
  signature?: string;
  zone?: string;
  chargeable_weight_kg?: number;
  quotes?: RateQuote[];
  live_accounts?: { id: string; carrier: string; name: string; auth_status: string }[];
}

export async function requestShippingQuotes(shipmentId: string): Promise<QuoteRequestResult> {
  const data = await callRpc<QuoteRequestResult>("request_shipping_quotes", { p_shipment_id: shipmentId });
  return data ?? { ok: false };
}

export async function selectShippingQuote(params: {
  shipmentId: string;
  quote: RateQuote;
  signature: string;
}): Promise<{ ok: boolean; quote_id?: string; error?: string }> {
  const q = params.quote;
  const data = await callRpc<{ ok: boolean; quote_id?: string; error?: string }>("select_shipping_quote", {
    p_shipment_id: params.shipmentId,
    p_carrier_service_id: q.carrier_service_id,
    p_carrier_account_id: q.carrier_account_id ?? null,
    p_service_name: q.service_name,
    p_carrier_code: q.carrier_code,
    p_zone: q.zone,
    p_currency: q.currency,
    p_cost: q.cost,
    p_chargeable_weight_kg: q.chargeable_weight_kg,
    p_eta_min_days: q.eta_min_days,
    p_eta_max_days: q.eta_max_days,
    p_rate_source: q.rate_source ?? "internal",
    p_signature: params.signature,
  });
  return data ?? { ok: false };
}

export interface CreateShipmentResult {
  ok: boolean;
  already_created?: boolean;
  mode?: "manual" | "live";
  queued?: boolean;
  status?: string;
  carrier_reference?: string | null;
  label_type?: string | null;
  error?: string;
}

export async function createShipment(params: {
  shipmentId: string;
  carrierAccountId?: string | null;
  quoteId?: string | null;
}): Promise<CreateShipmentResult> {
  const data = await callRpc<CreateShipmentResult>("create_shipment", {
    p_shipment_id: params.shipmentId,
    p_carrier_account_id: params.carrierAccountId ?? null,
    p_quote_id: params.quoteId ?? null,
  });
  return data ?? { ok: false };
}

// ---------------------------------------------------------------------------
// Package mutation — canonical SECURITY DEFINER RPCs.
//
// The browser no longer writes `shipment_packages` directly. Package CRUD goes
// through these RPCs, which derive the organisation from the shipment, enforce
// the postage-commit immutability boundary, and reuse the existing package-item
// ceiling validation (validate_package_items_fn trigger).
// ---------------------------------------------------------------------------

export interface ShipmentPackagePayload {
  package_number: string;
  weight: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  declared_value: number | null;
  tracking_number: string | null;
  items: { fulfilment_item_id?: string; product_name: string; qty: number }[];
}

export async function createShipmentPackage(
  shipmentId: string,
  payload: ShipmentPackagePayload,
): Promise<{ ok: boolean; id?: string; error?: string }> {
  const data = await callRpc<{ ok: boolean; id?: string; error?: string }>("create_shipment_package", {
    p_shipment_id: shipmentId,
    p_package_number: payload.package_number,
    p_weight: payload.weight,
    p_length_cm: payload.length_cm,
    p_width_cm: payload.width_cm,
    p_height_cm: payload.height_cm,
    p_declared_value: payload.declared_value,
    p_tracking_number: payload.tracking_number,
    p_items: payload.items,
  });
  return data ?? { ok: false };
}

export async function updateShipmentPackage(
  packageId: string,
  payload: ShipmentPackagePayload,
): Promise<{ ok: boolean; error?: string }> {
  const data = await callRpc<{ ok: boolean; error?: string }>("update_shipment_package", {
    p_package_id: packageId,
    p_package_number: payload.package_number,
    p_weight: payload.weight,
    p_length_cm: payload.length_cm,
    p_width_cm: payload.width_cm,
    p_height_cm: payload.height_cm,
    p_declared_value: payload.declared_value,
    p_tracking_number: payload.tracking_number,
    p_items: payload.items,
  });
  return data ?? { ok: false };
}

export async function deleteShipmentPackage(
  packageId: string,
): Promise<{ ok: boolean; error?: string }> {
  const data = await callRpc<{ ok: boolean; error?: string }>("delete_shipment_package", {
    p_package_id: packageId,
  });
  return data ?? { ok: false };
}

export interface ParcelInput {
  weight_kg: number;
  length_cm: number;
  width_cm: number;
  height_cm: number;
}

const VOLUMETRIC_DIVISOR = 5000; // cm3 -> kg (standard for air/express)

export function volumetricWeightKg(p: { length_cm?: number | null; width_cm?: number | null; height_cm?: number | null }): number {
  const l = Number(p.length_cm ?? 0);
  const w = Number(p.width_cm ?? 0);
  const h = Number(p.height_cm ?? 0);
  if (!l || !w || !h) return 0;
  return (l * w * h) / VOLUMETRIC_DIVISOR;
}

export function chargeableWeightKg(p: { weight?: number | null; length_cm?: number | null; width_cm?: number | null; height_cm?: number | null }): number {
  return Math.max(Number(p.weight ?? 0), volumetricWeightKg(p));
}

export async function seedShippingSetup(): Promise<{ ok: boolean; error?: string }> {
  const data = await callRpc<{ ok: boolean; error?: string }>("seed_shipping_setup");
  return data ?? { ok: false };
}

export async function rateShipment(originLocationId: string, destination: Record<string, unknown>, parcels: ParcelInput[]): Promise<RateQuote[]> {
  const data = await callRpc<RateQuote[]>("rate_shipment", {
    p_origin_location_id: originLocationId,
    p_destination: destination,
    p_parcels: parcels,
  });
  return data ?? [];
}

export function destinationFromAddress(addr: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!addr) return { country: "", state: "", postal_code: "", city: "" };
  return {
    country: addr.country ?? "",
    state: addr.state ?? addr.region ?? addr.province ?? "",
    postal_code: addr.postal_code ?? addr.zip ?? addr.postcode ?? "",
    city: addr.city ?? "",
  };
}

export function addressFromLocation(loc: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!loc) return null;
  return {
    name: loc.name ?? "",
    address_line1: loc.address_line1 ?? "",
    address_line2: loc.address_line2 ?? "",
    city: loc.city ?? "",
    state: loc.state ?? "",
    postal_code: loc.postal_code ?? "",
    country: loc.country ?? "",
    contact_name: loc.contact_name ?? "",
    contact_phone: loc.contact_phone ?? "",
  };
}

export function formatAddress(addr: Record<string, unknown> | null | undefined): string {
  if (!addr || typeof addr !== "object") return "—";
  const keys = [
    "name",
    "company",
    "address_line1",
    "address_line2",
    "line1",
    "line2",
    "street",
    "city",
    "state",
    "region",
    "postal_code",
    "zip",
    "postcode",
    "country",
  ];
  const parts: string[] = [];
  for (const k of keys) {
    const v = addr[k];
    if (typeof v === "string" && v.trim()) parts.push(v.trim());
  }
  return parts.length ? parts.join(", ") : "—";
}

export function formatEta(minDays: number | null, maxDays: number | null): string {
  if (minDays == null && maxDays == null) return "—";
  if (minDays != null && maxDays != null && minDays === maxDays) {
    return `${minDays} day${minDays === 1 ? "" : "s"}`;
  }
  return `${minDays ?? "?"}–${maxDays ?? "?"} days`;
}

export function zoneLabel(zone: string): string {
  if (zone === "local") return "Local";
  if (zone === "domestic") return "Domestic";
  if (zone === "international") return "International";
  return zone;
}